import { lazy } from 'react';

import type {
  IntegrationDefinition,
  IntegrationLoaderContext,
  NormalizedConnection,
} from '@mint-lib/integration-registry';
import {
  fetchOmniConnections,
  OMNI_URN,
  omniConnectionRepository,
} from '@mint-lib/omni-integration';
import type { QueryClient } from '@mint-lib/query-client';

async function prefetch(
  ctx: IntegrationLoaderContext,
): Promise<{ connections?: NormalizedConnection[] }> {
  const queryClient = ctx.queryClient as QueryClient;
  return {
    connections: await fetchOmniConnections(queryClient, OMNI_URN.zemanta),
  };
}

/**
 * Zemanta's own `IntegrationDefinition` — a one-way `dsp` integration built on
 * `@mint-lib/omni-integration`'s shared repository (list/verify/delete are
 * identical to the generic omni platforms), with its own basic (Client
 * ID/Client Secret) connect/reconnect flow and no OAuth capability.
 */
export function createZemantaIntegration(): IntegrationDefinition {
  return {
    meta: {
      urn: OMNI_URN.zemanta,
      displayName: 'Zemanta',
      i18nNamespace: '@mint-lib/zemanta-integration',
      channel: 'dsp',
      direction: 'one-way',
      showAdAccounts: true,
    },
    connection: {
      // Never imported eagerly — always via `React.lazy`, so `src/index.ts`
      // (which re-exports this factory) stays free of the form chunks.
      AddForm: lazy(() => import('@mint-lib/zemanta-integration/add-form')),
      ManageForm: lazy(
        () => import('@mint-lib/zemanta-integration/manage-form'),
      ),
      repository: omniConnectionRepository,
      prefetch,
    },
  };
}
